import { ensureArray, sentenceCase, SupportNeedRiskFactorValue } from '@lbcamden/shared-utils';
import groupBy from 'lodash/groupBy';
import maxBy from 'lodash/maxBy';
import sumBy from 'lodash/sumBy';
import { useCallback, useEffect, useMemo } from 'react';
import { Cell, Queries, MergeableStore as GenericStore } from 'tinybase';
import { useHasTable, useResultSortedRowIds, useTable } from 'tinybase/ui-react';
import { MergeableStore } from 'tinybase/with-schemas';

import { globalStoreTables } from '~/common/tinybase/globalStoreSchema';
import { userStoreTables } from '~/common/tinybase/userStoreSchema';
import { ArrearsArrangement } from '~/common/types/arrearsArrangements';
import { Property } from '~/common/types/buildings';
import { Case, CaseEvent, CaseSupportingOfficer, FullCase } from '~/common/types/cases';
import { ReferenceListItem } from '~/common/types/data';
import {
  ApplicationAllocation,
  ApplicationApplicant,
  FullHousingApplication,
  HousingApplication,
} from '~/common/types/housingApplications';
import { NoteTypeSubtype, UsedNoteType } from '~/common/types/notes';
import { PcfraDraft } from '~/common/types/pcfra';
import {
  Person,
  FullPerson,
  PcfraPerson,
  FullPcfraPerson,
  PersonRiskFactorItem,
  PersonAttributeItem,
  PersonAttributeCategoryCode,
} from '~/common/types/people';
import { SortBy } from '~/common/types/search';
import { ServiceRequest } from '~/common/types/serviceRequests';
import {
  GlobalStoreSchemas,
  GlobalStoreTables,
  GlobalStoreTableId,
  TableRow,
  UserStoreSchemas,
  UserStoreTables,
  UserStoreTableId,
} from '~/common/types/tinybase';
import { WorksOrder } from '~/common/types/worksOrders';
import {
  buildGetPersonNameFn,
  mergePersonWithAttributes,
  mergePersonWithContactDetails,
  mergePersonWithRiskFactors,
} from '~/common/utils/people';

import { useGlobalStore, useUserStore } from './storeSelectorHooks';
import { UiReactGlobal, UiReactUser } from './uiReactWithSchemas';

export type GetPersonFullNameFn = ReturnType<typeof useGetPersonFullName>;

type GlobalStore = NonNullable<ReturnType<typeof useGlobalStore>>;

const useGlobalStoreEntity = <TTableId extends GlobalStoreTableId>(
  tableId: TTableId,
  rowId: ScalarId,
) => {
  const store = useGlobalStore()?.store;
  const strRowId = String(rowId);
  const hasRow = UiReactGlobal.useHasRow(tableId, strRowId, store);
  const row = UiReactGlobal.useRow(tableId, strRowId, store); // Returns an empty object if the row doesn't exist

  return hasRow ? (row as TableRow<GlobalStoreTables, TTableId>) : undefined;
};

const useUserStoreEntity = <TTableId extends UserStoreTableId>(
  tableId: TTableId,
  rowId: ScalarId,
) => {
  const store = useUserStore()?.store;
  const strRowId = String(rowId);
  const hasRow = UiReactUser.useHasRow(tableId, strRowId, store);
  const row = UiReactUser.useRow(tableId, strRowId, store); // Returns an empty object if the row doesn't exist

  return hasRow ? (row as TableRow<UserStoreTables, TTableId>) : undefined;
};

const useGlobalStoreEntityList = <TTableId extends GlobalStoreTableId>(tableId: TTableId) => {
  const store = useGlobalStore()?.store;
  const entityTable = UiReactGlobal.useTable(tableId, store);

  const entityList = useMemo(
    () => Object.values(entityTable) as TableRow<GlobalStoreTables, TTableId>[],
    [entityTable],
  );

  return entityList;
};

// Serves both stores, so keeps a loose `store` boundary; the schema-typed
// wrappers below sit on top of it.
const queryStoreByCellValue = ({
  store,
  queries,
  tableId,
  cellId,
  value,
  selectCellIds,
  sort,
}: {
  store?: MergeableStore<GlobalStoreSchemas> | MergeableStore<UserStoreSchemas>;
  queries?: Queries;
  tableId: string;
  cellId: string;
  value: Cell | Cell[];
  selectCellIds: string[];
  sort?: SortBy;
}) => {
  const genericStore = store as unknown as GenericStore;
  // sort values to avoid duplicate queries
  const valueList = ensureArray(value).sort((a, b) => String(a).localeCompare(String(b)));
  const queryId = `${tableId}_${cellId}=${valueList.join()}`;

  const hasTable = useHasTable(tableId, genericStore);
  const entityTable = useTable(tableId, genericStore);

  const rowIds =
    useResultSortedRowIds(
      queryId,
      sort?.field,
      sort?.direction === 'desc',
      undefined,
      undefined,
      queries,
    ) || [];

  const results = useMemo(() => rowIds.map((rowId) => entityTable[rowId]), [entityTable, rowIds]);

  useEffect(() => {
    if (hasTable && queries && !queries.hasQuery(queryId)) {
      queries.setQueryDefinition(queryId, tableId, ({ select, where }) => {
        selectCellIds.forEach((id) => select(id));
        where((getTableCell) => valueList.includes(getTableCell(cellId) as Cell));
      });
    }
  }, [hasTable, queries, queryId]);

  return results;
};

const useQueryGlobalStoreByCellValue = <
  TTableId extends GlobalStoreTableId,
  TCellId extends StringKey<TableRow<GlobalStoreTables, TTableId>>,
>(
  tableId: TTableId,
  cellId: TCellId,
  value:
    | TableRow<GlobalStoreTables, TTableId>[TCellId]
    | TableRow<GlobalStoreTables, TTableId>[TCellId][],
  sort?: SortBy<StringKey<TableRow<GlobalStoreTables, TTableId>>>,
) => {
  const { store, queries } = useGlobalStore() || {};

  return queryStoreByCellValue({
    store,
    queries,
    tableId,
    cellId,
    value: value as Cell | Cell[],
    selectCellIds: Object.keys(globalStoreTables[tableId].shape),
    sort,
  }) as TableRow<GlobalStoreTables, TTableId>[];
};

const useQueryUserStoreByCellValue = <
  TTableId extends UserStoreTableId,
  TCellId extends StringKey<TableRow<UserStoreTables, TTableId>>,
>(
  tableId: TTableId,
  cellId: TCellId,
  value:
    | TableRow<UserStoreTables, TTableId>[TCellId]
    | TableRow<UserStoreTables, TTableId>[TCellId][],
  sort?: SortBy<StringKey<TableRow<UserStoreTables, TTableId>>>,
) => {
  const { store, queries } = useUserStore() || {};

  return queryStoreByCellValue({
    store,
    queries,
    tableId,
    cellId,
    value: value as Cell | Cell[],
    selectCellIds: Object.keys(userStoreTables[tableId].shape),
    sort,
  }) as TableRow<UserStoreTables, TTableId>[];
};

// User messages

export const useHasPendingUserMessages = () => {
  const userStore = useUserStore();
  const messages = UiReactUser.useTable('messages', userStore?.store);

  const hasPendingMessages = useMemo(() => !!userStore?.hasPendingMessages(), [messages]);

  return hasPendingMessages;
};

// User directory

export const useDirectoryUser = (camdenAccountName: string) =>
  useUserStoreEntity('userDirectory', camdenAccountName?.toUpperCase());

// Reference lists

export const useReferenceListSelectOptions = () =>
  useGlobalStore()?.referenceListSelectOptions as GlobalStore['referenceListSelectOptions'];

export const useReferenceListDisplayValue = () =>
  useGlobalStore()?.referenceListDisplayValue as GlobalStore['referenceListDisplayValue'];

export const useReferenceListItemsByBaseType = (baseType: string) =>
  useQueryGlobalStoreByCellValue('referenceLists', 'baseType', baseType) as ReferenceListItem[];

// Temp ID maps

export const useTempIdMap = (tempId: ScalarId = '') => useUserStoreEntity('tempIdMaps', tempId);

// Properties

export const usePropertyList = () => useGlobalStoreEntityList('properties') as Property[];

export const useProperty = (propertyId: ScalarId) =>
  useGlobalStoreEntity('properties', propertyId) as Property | undefined;

export const usePropertyDetails = (propertyId: ScalarId) =>
  useUserStoreEntity('propertyDetails', propertyId);

// PCFRA Properties

export const usePcfraPropertyDetails = (propertyId: ScalarId) =>
  useUserStoreEntity('pcfraPropertyDetails', propertyId);

// People

export const usePersonList = (propertyId: ScalarId) =>
  useQueryUserStoreByCellValue('people', 'propertyId', Number(propertyId));

/** Filters out any historic household members */
export const useCurrentPeopleList = (propertyId: ScalarId) => {
  const people = usePersonList(propertyId);

  return useMemo(() => people.filter(({ isHistoric }) => !isHistoric), [people]);
};

export const useTenantList = (propertyId: ScalarId) => {
  const people = usePersonList(propertyId);

  return useMemo(() => people.filter(({ isTenant }) => isTenant), [people]);
};

export const useHouseholdMemberList = (propertyId: ScalarId) => {
  const people = usePersonList(propertyId);

  return useMemo(() => people.filter(({ isTenant }) => !isTenant), [people]);
};

export const usePerson = (personId: ScalarId) => useUserStoreEntity('people', personId);

export const usePersonContactDetails = (personId: number | number[]) =>
  useQueryUserStoreByCellValue('contactDetails', 'personId', personId);

export const usePersonAttributes = (personId: number | number[]) =>
  useQueryUserStoreByCellValue('peopleAttributes', 'personId', personId) as PersonAttributeItem[];

export const usePeopleAttributes = (propertyId: number) =>
  useQueryUserStoreByCellValue(
    'peopleAttributes',
    'propertyId',
    propertyId,
  ) as PersonAttributeItem[];

export const useFullPerson = (personId: ScalarId): FullPerson | undefined => {
  const numPersonId = Number(personId);
  const person = usePerson(numPersonId);
  const contactDetails = usePersonContactDetails(numPersonId);
  const personAttributes = usePersonAttributes(numPersonId);

  return (
    person &&
    mergePersonWithAttributes(
      mergePersonWithContactDetails(person, contactDetails),
      personAttributes,
    )
  );
};

export const useGetPersonFullName = () => {
  const referenceListDisplayValue = useReferenceListDisplayValue();

  return useMemo(
    () => buildGetPersonNameFn(referenceListDisplayValue),
    [referenceListDisplayValue],
  );
};

export const useGetPersonDescription = () => {
  const referenceListDisplayValue = useReferenceListDisplayValue();

  const getHouseholdMemberDescription = (relationToTenant: string) =>
    relationToTenant && relationToTenant !== 'XXX' // Code for 'Unknown'
      ? sentenceCase(`Tenant's ${referenceListDisplayValue('RELATION', relationToTenant)}`)
      : 'Household member';

  return useCallback(
    ({ isTenant, relationToTenant }: Person | PcfraPerson) =>
      isTenant ? 'Tenant' : getHouseholdMemberDescription(relationToTenant),
    [referenceListDisplayValue],
  );
};

// PCFRA People

export const usePersonRiskFactors = (personId: number | number[]) =>
  useQueryUserStoreByCellValue('peopleRiskFactors', 'personId', personId) as PersonRiskFactorItem[];

/** Merges each person with their risk factors and contact details */
export const usePcfraPeopleList = (propertyId: ScalarId): FullPcfraPerson[] => {
  const referenceListDisplayValue = useReferenceListDisplayValue();

  const people = useQueryUserStoreByCellValue('pcfraPeople', 'propertyId', Number(propertyId), {
    field: 'isTenant',
    direction: 'desc',
  });

  const personIdList = people.map(({ personId }) => personId);

  const allContactDetails = usePersonContactDetails(personIdList);
  const allRiskFactors = usePersonRiskFactors(personIdList);
  const allAttributes = usePersonAttributes(personIdList);

  return useMemo<FullPcfraPerson[]>(
    () =>
      people.map((person) => {
        const personContactDetails = allContactDetails.filter(
          ({ personId }) => personId === person.personId,
        );
        const personRiskFactors = allRiskFactors.filter(
          ({ personId }) => personId === person.personId,
        );
        const personSupportNeedRiskFactors: SupportNeedRiskFactorValue[] = allAttributes
          .filter(
            ({ personId, categoryCode }) =>
              personId === person.personId &&
              categoryCode === PersonAttributeCategoryCode.SupportNeedRiskFactor,
          )
          .map(({ attributeCode }) => ({
            attributeCode,
            description: referenceListDisplayValue('PERSON_ATTRIBUTE', attributeCode),
          }));

        return mergePersonWithRiskFactors(
          mergePersonWithContactDetails(person, personContactDetails),
          personRiskFactors,
          personSupportNeedRiskFactors,
        );
      }),
    [people, allContactDetails, allRiskFactors],
  );
};

export const usePcfraPerson = (personId: ScalarId): FullPcfraPerson | undefined => {
  const numPersonId = Number(personId);
  const person = useUserStoreEntity('pcfraPeople', numPersonId);
  const contactDetails = usePersonContactDetails(numPersonId);
  const riskFactors = usePersonRiskFactors(numPersonId);

  return useMemo(
    () =>
      person &&
      mergePersonWithRiskFactors(
        mergePersonWithContactDetails(person, contactDetails),
        riskFactors,
      ),
    [person, contactDetails, riskFactors],
  );
};

// Notes

export const useAllNotesList = (propertyId: number) =>
  useQueryUserStoreByCellValue('notes', 'propertyId', propertyId);

export const useEntityNoteList = (type: UsedNoteType, entityRef: ScalarId | ScalarId[]) => {
  const notesOfType = useQueryUserStoreByCellValue('notes', 'type', type);

  return useMemo(
    () => notesOfType.filter((note) => ensureArray(entityRef).map(String).includes(note.entityRef)),
    [notesOfType, entityRef],
  );
};

export const useNote = (noteId: string) => useUserStoreEntity('notes', noteId);

export const useFireSafetyNoteList = (personId: number) =>
  useQueryUserStoreByCellValue('fireSafetyNotes', 'personId', personId);

export const useFireSafetyNote = (noteId: string) => useUserStoreEntity('fireSafetyNotes', noteId);

export const useNoteSubtypeSelectOptions = (type?: UsedNoteType) => {
  const referenceListSelectOptions = useReferenceListSelectOptions();

  const noteTypeSubtypes = useQueryGlobalStoreByCellValue(
    'noteTypeSubtypes',
    'type',
    type ?? '',
  ) as NoteTypeSubtype[];

  return useMemo(() => {
    if (!type) {
      return [];
    }
    const subtypes = noteTypeSubtypes.map(({ subtype }) => subtype);

    return referenceListSelectOptions('NOTE_SUBTYPE').filter(({ value }) =>
      subtypes.includes(value),
    );
  }, [referenceListSelectOptions, noteTypeSubtypes]);
};

// Visits

export const useVisitList = (propertyId: number) =>
  useQueryUserStoreByCellValue('visits', 'propertyId', propertyId);

export const useVisit = (visitId: string) => useUserStoreEntity('visits', visitId);

export const useLastSuccessfulPropertyVisitDate = (propertyId: number) => {
  const visits = useVisitList(propertyId);
  return useMemo(() => {
    const successfulVisits = visits.filter((v) => v.outcome === 'success:data-validated');
    return maxBy(successfulVisits, 'timestamp')?.timestamp;
  }, [visits]);
};

// Service Requests

export const useServiceRequest = (serviceRequestId: ScalarId) =>
  useUserStoreEntity('serviceRequests', serviceRequestId) as ServiceRequest | undefined;

export const useServiceRequestsList = (propertyId: number, buildingId: string) => {
  const serviceRequests = useQueryUserStoreByCellValue(
    'serviceRequests',
    'buildingId',
    buildingId,
  ) as ServiceRequest[];

  return useMemo(
    () =>
      serviceRequests.filter(
        (serviceRequest) =>
          !serviceRequest.isPropertyRequest || serviceRequest.propertyId === propertyId,
      ),
    [serviceRequests, propertyId],
  );
};

export const useWorksOrdersList = (serviceRequestId: number) =>
  useQueryUserStoreByCellValue('worksOrders', 'serviceRequestId', serviceRequestId) as WorksOrder[];

// Cases

export const useCaseList = (propertyId: ScalarId) =>
  useQueryUserStoreByCellValue('cases', 'propertyId', Number(propertyId)) as Case[];

export const useLeadOfficerOpenedCaseList = (leadOfficer: string) =>
  useQueryUserStoreByCellValue('cases', 'leadOfficer', leadOfficer) as Case[];

export const useCase = (caseId: ScalarId) =>
  useUserStoreEntity('cases', caseId) as Case | undefined;

export const useCaseEvents = (caseId: number) =>
  useQueryUserStoreByCellValue('caseEvents', 'caseId', caseId, {
    field: 'created',
    direction: 'desc',
  }) as CaseEvent[];

export const useCaseSupportingOfficers = (caseId: number) =>
  useQueryUserStoreByCellValue('caseSupportingOfficers', 'caseId', caseId, {
    field: 'officer',
    direction: 'asc',
  }) as CaseSupportingOfficer[];

export const useFullCase = (caseId: number): FullCase | undefined => {
  const caseItem = useCase(caseId);
  const events = useCaseEvents(caseId);
  const supportingOfficerItems = useCaseSupportingOfficers(caseId);

  const supportingOfficers = supportingOfficerItems.map(({ officer }) => officer);

  return caseItem && { ...caseItem, events, supportingOfficers };
};

// Housing applications

export const useApplicationApplicantsList = (applicationId: number | number[]) =>
  useQueryUserStoreByCellValue(
    'applicationApplicants',
    'applicationId',
    applicationId,
  ) as ApplicationApplicant[];

export const useApplicationAllocationList = (applicationId: number | number[]) =>
  useQueryUserStoreByCellValue(
    'applicationAllocations',
    'applicationId',
    applicationId,
  ) as ApplicationAllocation[];

export const useHousingApplicationList = (propertyId: number): FullHousingApplication[] => {
  const applications = useQueryUserStoreByCellValue(
    'housingApplications',
    'propertyId',
    propertyId,
  ) as HousingApplication[];

  const applicationIds = applications.map((app) => app.applicationId);

  const applicants = useApplicationApplicantsList(applicationIds);
  const allocations = useApplicationAllocationList(applicationIds);

  return useMemo(() => {
    const applicantsMap = groupBy(applicants, 'applicationId');
    const allocationsMap = groupBy(allocations, 'applicationId');

    return applications.map<FullHousingApplication>((app) => ({
      ...app,
      applicantPersonIds: applicantsMap[app.applicationId]?.map((item) => item.personId) || [],
      points: sumBy(allocationsMap[app.applicationId], 'points'),
    }));
  }, [applications, applicants, allocations]);
};

export const useHousingApplication = (applicationId: ScalarId) =>
  useUserStoreEntity('housingApplications', applicationId) as HousingApplication | undefined;

// Arrears arrangements

export const useArrearsArrangementsList = (propertyId: number) =>
  useQueryUserStoreByCellValue(
    'arrearsArrangements',
    'propertyId',
    propertyId,
  ) as ArrearsArrangement[];

export const useArrearsArrangement = (arrangementId: ScalarId) =>
  useUserStoreEntity('arrearsArrangements', arrangementId) as ArrearsArrangement | undefined;

// PCFRAs

export const usePcfraReferrer = (personId: ScalarId) =>
  useUserStoreEntity('pcfraReferrers', personId);

export const usePcfraDraft = (draftId: string): PcfraDraft | undefined => {
  const row = useUserStoreEntity('pcfraDrafts', draftId);

  // The row object is ui-react's cached snapshot; PcfraCreateEditPage merges
  // prefill values into draft.values in place, so hand out an owned copy
  return useMemo(() => row && (structuredClone(row) as PcfraDraft), [row]);
};

export const usePcfraDraftList = (propertyId: ScalarId) =>
  useQueryUserStoreByCellValue('pcfraDrafts', 'propertyId', Number(propertyId));

export const usePcfraDocumentsList = (personId: ScalarId) =>
  useQueryUserStoreByCellValue('pcfraDocuments', 'personId', Number(personId));
